import React from 'react';
import {
  Box,
  Typography,
  useTheme,
  alpha,
  Skeleton,
  Card,
  CardContent,
} from '@mui/material';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { useTranslation } from 'react-i18next';

const GOLD = '#c9a96e';

const STATUS_COLORS = {
  draft: '#78716c',
  in_review: '#f59e0b',
  pending_signature: '#3b82f6',
  signed: '#22c55e',
};

const PortfolioBreakdown = ({ lifecycle }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  if (lifecycle.loading) {
    return (
      <Card
        elevation={0}
        sx={{
          borderRadius: '12px',
          border: `1px solid ${theme.palette.divider}`,
          background: theme.palette.background.paper,
          height: '100%',
        }}
      >
        <CardContent sx={{ p: 2.5 }}>
          <Skeleton variant="text" width={160} height={24} sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Skeleton variant="circular" width={160} height={160} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  const stages = lifecycle.data?.stages || {};
  const totalContracts = lifecycle.data?.total_contracts || 0;

  const labels = {
    draft: t('clmDashboard.portfolio.draft', 'Drafts'),
    in_review: t('clmDashboard.portfolio.inReview', 'In Review'),
    pending_signature: t('clmDashboard.portfolio.signing', 'Signing'),
    signed: t('clmDashboard.portfolio.signed', 'Signed'),
  };

  const chartData = Object.entries(stages)
    .map(([key, val]) => ({
      name: labels[key] || key,
      value: val?.total || 0,
      color: STATUS_COLORS[key] || '#78716c',
    }))
    .filter((d) => d.value > 0);

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: '12px',
        border: `1px solid ${theme.palette.divider}`,
        background: theme.palette.background.paper,
        height: '100%',
        boxShadow:
          theme.palette.mode === 'dark'
            ? '0 2px 8px rgba(0, 0, 0, 0.3)'
            : '0 2px 8px rgba(0, 0, 0, 0.04)',
      }}
    >
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            fontSize: '0.85rem',
            color: GOLD,
            mb: 2,
            letterSpacing: '0.02em',
          }}
        >
          {t('clmDashboard.portfolioBreakdown', 'Portfolio Breakdown')}
        </Typography>

        {totalContracts === 0 ? (
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.secondary, textAlign: 'center', py: 4 }}
          >
            {t('clmDashboard.noData', 'No data available')}
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ width: 160, height: 160, flexShrink: 0 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={45}
                    outerRadius={72}
                    paddingAngle={2}
                    dataKey="value"
                    stroke="none"
                  >
                    {chartData.map((entry, index) => (
                      <Cell key={index} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      background: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 8,
                      fontSize: '0.8rem',
                    }}
                    formatter={(value) => [value, null]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Box>

            {/* Legend */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
              {chartData.map((entry) => (
                <Box key={entry.name} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '3px',
                      background: entry.color,
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{ flex: 1, fontSize: '0.75rem', color: theme.palette.text.secondary }}
                  >
                    {entry.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 700, fontSize: '0.8rem', color: theme.palette.text.primary }}
                  >
                    {entry.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default PortfolioBreakdown;
